import { useCallback, useMemo } from 'react';

import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useCreateRequestEventMutation } from '@/app/services/issues';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import {
  RequestEventFragment,
  RequestEventType,
  RequestFragment,
} from '@/rtk-query.gql';

import { ICONS } from './constants';

type MarkAsInProgressProps = {
  request: RequestFragment & { events: RequestEventFragment[] };
};

export const MarkAsInProgress = ({ request }: MarkAsInProgressProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [createEvent, { isLoading }] = useCreateRequestEventMutation();

  const markInProgress = useCallback(async () => {
    try {
      await createEvent({
        content: '',
        requestId: request.id,
        type: RequestEventType.IN_PROGRESS,
        refRequestId: null,
      });
      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.success'),
          detail: t('issues.markedIssueAsInProgress'),
          life: ToastLife.DEFAULT,
        }),
      );
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: t('issues.errorMarkingAsInProgress') + error,
          life: ToastLife.DEFAULT,
        }),
      );
    }
  }, [createEvent, dispatch, request.id, t]);

  const wasPreviouslyInProgress = useMemo(
    () => request.events.some((e) => e.type === RequestEventType.IN_PROGRESS),
    [request.events],
  );

  return (
    <Button
      label={
        wasPreviouslyInProgress
          ? t('common.reopen')
          : t('issues.markAsInProgress')
      }
      className="status-button status-button--in-progress"
      icon={ICONS[RequestEventType.IN_PROGRESS]}
      loading={isLoading}
      disabled={isLoading}
      onClick={markInProgress}
    />
  );
};
